import { useState, useEffect } from "react";
import axios from "axios";

// @mui material components
import Grid from "@mui/material/Grid";
import Rating from "@mui/material/Rating";
import CircularProgress from "@mui/material/CircularProgress";
import GoogleIcon from "@mui/icons-material/Google";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// Material Kit 2 React examples
import DefaultReviewCard from "examples/Cards/ReviewCards/DefaultReviewCard";

// Data
import textData from "data/textData";

function GoogleReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [error, setError] = useState(null);

  const { google: googleTexts } = textData.presentationPage.reviews;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // WORKER URL (URL Universal para tus sitios)
        const WORKER_URL = "https://google-reviews-worker.heduardoaa.workers.dev";

        // eslint-disable-next-line no-console
        console.log("Obteniendo reseñas desde Cloudflare Worker...");

        const response = await axios.get(WORKER_URL);
        const { data } = response; // Corregido: Desestructuración

        // Validar si el worker devolvió error (ej. 403 o 500)
        if (data.error) {
          throw new Error(data.error);
        }

        // 1. Establecer Reseñas
        if (data.reviews && data.reviews.length > 0) {
          setReviews(data.reviews.slice(0, 5)); // Tomamos las 5 primeras
        }

        // 2. Establecer Ratings
        if (data.rating && data.total_reviews) {
          setAverageRating(data.rating);
          setTotalReviews(data.total_reviews);
        }

        // Si no hay datos, mostrar error (o fallback visual)
        if ((!data.reviews || data.reviews.length === 0) && !data.rating) {
          setError(googleTexts.errorNoReviews || "No hay reseñas disponibles.");
        }

      } catch (err) {
        // eslint-disable-next-line no-console
        // eslint-disable-next-line no-console
        console.error("Error al cargar reseñas:", err.message);
        setError(googleTexts.errorNetwork || "Lo sentimos, no pudimos cargar las reseñas en este momento.");
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [googleTexts]);

  if (loading) {
    return (
      <MKBox display="flex" justifyContent="center" alignItems="center" sx={{ height: "20rem" }}>
        <CircularProgress />
      </MKBox>
    );
  }

  if (error) {
    return (
      <MKTypography color="error" sx={{ textAlign: "center" }}>
        {error}
      </MKTypography>
    );
  }

  return (
    <MKBox component="section" py={6}>
      {totalReviews > 0 && !error && (
        <Grid container justifyContent="center" sx={{ mb: 4, textAlign: "center" }}>
          <Grid item xs={12} md={6} lg={4}>
            <MKBox
              p={2}
              borderRadius="lg"
              sx={{
                border: ({ borders: { borderWidth, borderColor } }) =>
                  `${borderWidth[1]} solid ${borderColor}`,
              }}
            >
              <MKBox display="flex" justifyContent="center" alignItems="center" gap={1}>
                <GoogleIcon />
                <MKTypography variant="h6" fontWeight="bold">
                  Google Rating
                </MKTypography>
              </MKBox>
              <MKTypography variant="h4" fontWeight="bold" color="info" sx={{ my: 1 }}>
                {averageRating.toFixed(1)}
              </MKTypography>
              <Rating value={averageRating} readOnly precision={0.1} />
              <MKTypography variant="body2" color="text" sx={{ mt: 1 }}>
                Basado en {totalReviews} reseñas
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>
      )}
      <Grid container spacing={3}>
        {reviews.map((review) => (
          <Grid item xs={12} md={6} lg={4} key={review.time}>
            <DefaultReviewCard
              name={
                <>
                  {review.author_name}
                  <MKTypography variant="caption" color="text" component="p" display="block">
                    {review.relative_time_description}
                  </MKTypography>
                </>
              }
              image={review.profile_photo_url}
              review={review.text}
              rating={<Rating value={review.rating} readOnly />}
            />
          </Grid>
        ))}
        {reviews.length > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <MKBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              textAlign="center"
              height="100%"
              p={3}
              borderRadius="lg"
              sx={{ background: ({ palette: { gradients } }) => gradients.info.main }}
            >
              <MKTypography variant="h5" color="white" fontWeight="bold">
                {googleTexts.cardTitle}
              </MKTypography>
              <MKTypography variant="body2" color="white" my={3}>
                {googleTexts.cardDescription}
              </MKTypography>
              <MKButton
                variant="contained"
                color="white"
                component="a"
                href="https://search.google.com/local/writereview?placeid=ChIJ90ZxqBICzoURy3O1zxX29yY"
                target="_blank"
                rel="noopener noreferrer"
              >
                {googleTexts.cardButton}
              </MKButton>
            </MKBox>
          </Grid>
        )}
      </Grid>
    </MKBox>
  );
}

export default GoogleReviews;
